import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { isEmpty, omit } from 'lodash/fp';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type UserGroupListDataFragment } from '../../../api/fragments/UserGroupListData';
import {
    CreateUserGroupDocument,
    type CreateUserGroupMutation,
    type CreateUserGroupMutationVariables,
} from '../../../api/mutations/createUserGroup';
import { useListAvailableUserGroupsQuery } from '../../../api/queries/listAvailableUserGroups';
import { useListCompaniesForSelectionQuery } from '../../../api/queries/listCompaniesForSelection';
import { type UserGroupSettings } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { InputField } from '../../../components/fields/ci';
import SelectField from '../../../components/fields/SelectField';
import SwitchField from '../../../components/fields/SwitchField';
import { useThemeComponents } from '../../../themes';
import useHandleError from '../../../utilities/useHandleError';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import SelectUserGroupsWithSearchField from '../UserGroupDetailPage/SelectUserGroupsWithSearchField';

export type CreateUserGroupModalProps = { visible: boolean; onClose: () => void };

type FormValues = UserGroupSettings & { companyId: string };

const formValidator = validators.compose(
    validators.requiredString('displayName'),
    validators.requiredString('description'),
    validators.requiredString('companyId')
);

const CreationForm = () => {
    const { t } = useTranslation(['userGroupList']);
    const { yesNoSwitch } = useSystemSwitchData();

    // get company context
    const company = useCompany(true);

    const { data } = useListCompaniesForSelectionQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: company?.id,
            },
        },
    });

    const companies = data?.list?.items || [];

    const { data: userGroupData } = useListAvailableUserGroupsQuery({
        fetchPolicy: 'cache-and-network',
    });

    const { dataSource } = useMemo(() => {
        const userGroups = userGroupData?.page.items || [];

        return {
            dataSource: userGroups,
        };
    }, [userGroupData]);

    const [userGroups, setUserGroups] = useState<UserGroupListDataFragment[]>([]);
    const { values } = useFormikContext<FormValues>();

    // ensure that superior group options are from the company ID selected
    useEffect(() => {
        if (!isEmpty(values.companyId)) {
            setUserGroups(dataSource.filter(group => group.company.id === values.companyId));
        }
    }, [dataSource, values]);

    return (
        <>
            <InputField
                {...t('userGroupList:creationModal.fields.displayName', { returnObjects: true })}
                name="displayName"
                required
            />
            <InputField
                {...t('userGroupList:creationModal.fields.description', { returnObjects: true })}
                name="description"
                required
            />
            <SelectField
                {...t('userGroupList:creationModal.fields.company', { returnObjects: true })}
                name="companyId"
                options={companies.map(company => ({ value: company.id, label: company.displayName }))}
                required
                showSearch
            />
            <SwitchField
                {...t('userGroupList:creationModal.fields.isParallel', { returnObjects: true })}
                name="isParallel"
                {...yesNoSwitch}
            />
            <SwitchField
                {...t('userGroupList:creationModal.fields.isActive', { returnObjects: true })}
                name="isActive"
                {...yesNoSwitch}
            />
            <SelectUserGroupsWithSearchField
                {...t('userGroupList:creationModal.fields.parentUserGroup', { returnObjects: true })}
                mode="multiple"
                name="superiorUserGroupIds"
                style={{ width: '100%' }}
                userGroups={userGroups}
            />
        </>
    );
};

const initialValues: FormValues = {
    companyId: '',
    description: '',
    displayName: '',
    isParallel: false,
    superiorUserGroupIds: [],
    isActive: false,
};

const CreateUserGroupModal = ({ visible, onClose }: CreateUserGroupModalProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation(['userGroupList']);
    const apolloClient = useApolloClient();
    const { Modal } = useThemeComponents();
    const validate = useValidator(formValidator);

    const onSubmit = useHandleError<FormValues>(
        async values => {
            message.loading({
                content: t('userGroupList:creationModal.messages.submitting'),
                duration: 0,
                key: 'primary',
            });

            if (!values.companyId) {
                throw new Error('Company ID is missing');
            }

            // call mutation to create the new pack
            const { data } = await apolloClient
                .mutate<CreateUserGroupMutation, CreateUserGroupMutationVariables>({
                    mutation: CreateUserGroupDocument,
                    variables: { companyId: values.companyId, settings: omit(['companyId'], values) },
                })
                .finally(() => {
                    // always clean up
                    message.destroy('primary');
                });

            // show successful message
            message.success({
                content: t('userGroupList:creationModal.messages.success'),
                key: 'primary',
            });

            // then browse to the new user group
            navigate(`/admin/accesses/userGroups/${data.userGroup.id}`);
        },
        [apolloClient, t, navigate]
    );

    return (
        <Modal
            cancelText={t('userGroupList:creationModal.cancelText')}
            okButtonProps={{ htmlType: 'submit', form: 'userGroupCreation' }}
            okText={t('userGroupList:creationModal.okText')}
            onCancel={onClose}
            open={visible}
            title={t('userGroupList:creationModal.title')}
            destroyOnClose
        >
            <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="userGroupCreation"
                        layout="vertical"
                        name="userGroupCreation"
                        onSubmitCapture={handleSubmit}
                    >
                        <CreationForm />
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default CreateUserGroupModal;

export const useCreationModal = () => {
    const [visible, setVisible] = useState(false);
    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    const render = () => <CreateUserGroupModal onClose={actions.close} visible={visible} />;

    return { visible, ...actions, render };
};
